import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { CalendarDaysIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    fetchCountry,
    fetchCity,
    fetchOffice,
} from "@/store/slices/internalUsersSlice";
import { Input, Typography } from "@material-tailwind/react";
import Toaster from "@/components/Toaster";
import { assignOrderToCRManager, fetchCRManager, fetchCRTeamMember, resetStatus } from "@/store/slices/orderSlice";
import { getSettingData } from "@/store/slices/settingSlice";
import moment from "moment";
import useHasPermission from "@/components/useHasPermission";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};
const priorities = [
    {
        label: "High",
        value: "high"
    },
    {
        label: "Medium",
        value: "medium"
    },
    {
        label: "Low",
        value: "low"
    }
];
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = (t) => yup.object().shape({
    city_members: yup.mixed().nullable(),
    country_id: yup.string().required(t('country_is_required')),
    city_id: yup.string().required(t('city_is_required')),
    office_id: yup.string().required(t('office_is_required')),
    city_representative_id: yup.string().required(t('logistics_manager_is_required')),
    deadline: yup.string().required(t('deadline_date_required')),
    priority: yup.string().required(t('priority_required')),
    notes: yup.string()
        .required(t('note_is_required'))
        .matches(notBlankRegex, t('note_cannot_be_empty'))
        .matches(noHTMLRegex, t('html_tags_not_allowed'))
        .matches(noCodeRegex, t('code_not_allowed'))
        .matches(noConsecutiveSpecialChars, t('no_consecutive_special_chars'))
        .matches(noLeadingTrailingPunct, t('no_leading_trailing_punctuation'))
        .min(2, t('note_min_length'))
        .max(100, t('note_max_length')),

    commission_type: yup.string().required(t('commission_type_is_required')),
    commission_city_manager: yup
        .number()
        .typeError(t('logistic_manager_commission_required'))
        .min(0, t('minimum_value_is_0'))
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, t('percentage_cannot_exceed_100')),
            otherwise: (s) => s.max(99999, t('commission_cannot_exceed_99999')),
        }),
    commission_city_rep: yup
        .number()
        .nullable()
        .when("city_members", {
            is: (value) => value,
            then: (schema) =>
                schema
                    .typeError(t('city_rep_commission_required'))
                    .required(t('city_rep_commission_required'))
                    .min(0, t('minimum_value_is_0'))
                    .when("commission_type", {
                        is: "percentage",
                        then: (s) => s.max(100, t('percentage_cannot_exceed_100')),
                        otherwise: (s) =>
                            s.max(99999, t('commission_cannot_exceed_99999')),
                    }),
            otherwise: (schema) => schema.notRequired(),
        }),
    time_slot_from_city_manager: yup
        .string()
        .required(t('logistic_manager_timeslot_required'))
        .test("from>=now", t('logistic_manager_from_in_past'), function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", t('logistic_manager_from_exceed_deadline'), function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),
    time_slot_to_city_manager: yup
        .string()
        .required(t('logistic_manager_timeslot_required'))
        .test("to<=deadline", t('logistic_manager_to_exceed_deadline'), function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        })
        .test("to>=from", t('logistic_manager_to_before_from'), function (v) {
            const from = this.parent?.time_slot_from_city_manager;
            if (!v || !from) return true;
            return new Date(v).getTime() >= new Date(from).getTime();
        }),

    time_slot_from_city_rep: yup
        // .string()
        // .nullable()
        // .when("city_members", {
        //     is: (value) => !!value,
        //     then: (schema) =>
        //         schema
        //             .test(
        //                 "from>=now",
        //                 t('logistic_member_from_in_past'),
        //                 function (v) {
        //                     if (!v) return true;
        //                     return new Date(v).getTime() >= new Date().getTime();
        //                 }
        //             )
        //             .test(
        //                 "from<=deadline",
        //                 t('logistic_member_from_exceed_deadline'),
        //                 function (v) {
        //                     const deadline = this.parent?.deadline;
        //                     if (!v || !deadline) return true;
        //                     return new Date(v).getTime() <= new Date(deadline).getTime();
        //                 }
        //             ),
        //     otherwise: (schema) => schema.notRequired(),
        // }),
        .string()
        .required(t('logistic_member_timeslot_required'))
        .test("from>=now", t('logistic_member_from_in_past'), function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", t('logistic_member_from_exceed_deadline'), function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),

    time_slot_to_city_rep: yup
        // .string()
        // .nullable()
        // .when("city_members", {
        //     is: (value) => !!value,
        //     then: (schema) =>
        //         schema
        //             .test(
        //                 "to-required-if-from",
        //                 t('logistic_member_to_required'),
        //                 function (v) {
        //                     const from = this.parent?.time_slot_from_city_rep;

        //                     if (from && !v) {
        //                         return this.createError({
        //                             message:
        //                                 t('logistic_member_to_required'),
        //                         });
        //                     }

        //                     return true;
        //                 }
        //             )
        //             .test(
        //                 "to<=deadline",
        //                 t('logistic_member_to_exceed_deadline'),
        //                 function (v) {
        //                     const deadline = this.parent?.deadline;
        //                     if (!v || !deadline) return true;
        //                     return new Date(v).getTime() <= new Date(deadline).getTime();
        //                 }
        //             )
        //             .test(
        //                 "to>=from",
        //                 t('logistic_member_to_before_from'),
        //                 function (v) {
        //                     const from = this.parent?.time_slot_from_city_rep;
        //                     if (!v || !from) return true;
        //                     return new Date(v).getTime() >= new Date(from).getTime();
        //                 }
        //             ),
        //     otherwise: (schema) => schema.notRequired(),
        // }),
        .string()
        .required(t('logistic_member_timeslot_required'))
        .test("to<=deadline", t('logistic_member_to_exceed_deadline'), function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        })
        .test("to>=from", t('logistic_member_to_before_from'), function (v) {
            const from = this.parent?.time_slot_from_city_rep;
            if (!v || !from) return true;
            return new Date(v).getTime() >= new Date(from).getTime();
        }),
});

export function AssignCRManagerModal({ onClose, orderId }) {
    const dispatch = useDispatch();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');
    const { settings } = useSelector((state) => state.setting);

    const hasAddMemberPermission = useHasPermission(`member.add`);


    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            city_members: null,
            order_ids: [orderId],
            commission_type: "percentage",
            notes: null,
            time_slot_from_city_manager: "",
            time_slot_to_city_manager: "",
            time_slot_from_city_rep: "",
            time_slot_to_city_rep: "",
        }, resolver: yupResolver(schema(t))
    });

    const { cities, countries, offices } = useSelector((state) => state.internalUser);

    const { cr_managers, cr_team, loading } = useSelector((state) => state.order);

    useEffect(() => {
        dispatch(fetchCountry());
        dispatch(getSettingData());
    }, [dispatch]);

    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                city_members: selectedUsers,
            };
            const result = await dispatch(assignOrderToCRManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(t('order_assigned_success'));
                dispatch(resetStatus());
                reset();
                onClose();
            } else if (!result?.status && result?.data?.users_on_leave) {
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || t('assign_user_on_leave'));
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || ct('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t('failed_to_assign_order'));
            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => {
        try {
            const payload = {
                ...getValues(),
                sales_members: [selectedUsers],
                force_assign: true,
            };
            const result = await dispatch(assignOrderToCRManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || t('order_assigned_success'));
                dispatch(resetStatus());
                reset();
                onClose();
                setLeaveConfirmation(null);
            } else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || ct('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || t('failed_to_assign_order'));
            dispatch(resetStatus());
        }
    }

    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    const deadlineValue = watch("deadline");
    const isCityMemberSelected = !!watch("city_members");

    const [selectedUsers, setSelectedUsers] = useState([]);

    // const handleChange = (userId) => {
    //     setSelectedUsers((prev) =>
    //         prev.includes(userId)
    //             ? prev.filter((id) => id !== userId)
    //             : [...prev, userId]
    //     );
    //     setValue("city_members", selectedUsers, { shouldValidate: true });
    // };

    const handleChange = (userId) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );

        setValue("city_members", userId, { shouldValidate: true });

        // When a Logistics Representative Team Member is selected,
        // set the member's "To" time equal to the Order Deadline.
        if (userId) {
            const deadline = getValues("deadline");

            if (deadline) {
                setValue("time_slot_to_city_rep", deadline, {
                    shouldValidate: true,
                });
            }
        }
    };


    const commissionType = watch("commission_type");
    const defaultCommissions = [
        { text: t('set_commission_for_logistic_member'), value: 0, key: "commission_city_rep", fromKey: "time_slot_from_city_rep", toKey: "time_slot_to_city_rep" },
        { text: t('set_commission_for_logistic_manager'), value: 0, key: "commission_city_manager", fromKey: "time_slot_from_city_manager", toKey: "time_slot_to_city_manager" },
    ];

    useEffect(() => {
        if (!settings) return;

        const value =
            commissionType === "percentage"
                ? settings.percentage_commission
                : settings.flat_commission;

        reset((prev) => ({
            ...prev,
            commission_city_manager: value,
            commission_city_rep: value,
        }));
    }, [settings, commissionType, reset]);

    useEffect(() => {
        if (countries?.length > 0 && !watch('country_id')) {
            const saudiArabia = countries.find(c => c.label === 'Saudi Arabia');
            if (saudiArabia) {
                setValue('country_id', saudiArabia.value, { shouldValidate: true });
                dispatch(fetchCity(saudiArabia.value));
            }
        }
    }, [countries, setValue, dispatch, watch]);

    // useEffect(()=>{
    //     let value = watch('time_slot_to_city_manager');
    //     setValue('time_slot_from_city_rep', value);
    // },[watch('time_slot_to_city_manager')]);

    // useEffect(() => {
    //     if (!isCityMemberSelected) return;

    //     const value = watch("time_slot_to_city_manager");
    //     setValue("time_slot_from_city_rep", value);
    // }, [watch("time_slot_to_city_manager"), isCityMemberSelected]);

    // useEffect(() => {
    //     if (!isCityMemberSelected) return;

    //     const value = watch("time_slot_to_city_manager");
    //     setValue("time_slot_from_city_rep", value);
    // }, [watch("time_slot_to_city_manager"), isCityMemberSelected]);

    useEffect(() => {
        const value = watch("time_slot_to_city_rep");
        if (!value) return;

        setValue("time_slot_from_city_manager", value, { shouldValidate: true });
    }, [watch("time_slot_to_city_rep"), isCityMemberSelected]);

    useEffect(() => {
        if (!deadlineValue) return;
        setValue("time_slot_to_city_manager", deadlineValue, { shouldValidate: true });
    }, [deadlineValue, setValue]);

    return (
        <Dialog
            open={true}
            handler={onClose}
            size="md"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t('assign_to_logistics_department_title')}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 max-h-[65vh] overflow-y-auto">
                    <DialogBody>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">
                            {/* Country selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('country_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <input type="hidden" {...register('country_id')} />
                                <Select
                                    placeholder={t('select_country_placeholder')}
                                    options={countries}
                                    onChange={(c) => {
                                        setValue("country_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchCity(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    value={countries.find(c => c.value === watch('country_id')) || null}
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{errors.country_id.message}</p>
                                )}
                            </div>

                            {/* City selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('city_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_city_placeholder')}
                                    options={cities}
                                    onChange={(c) => {
                                        setValue("city_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{errors.city_id.message}</p>
                                )}
                            </div>

                            {/* Office selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('office_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_office_placeholder')}
                                    options={offices}
                                    onChange={(o) => {
                                        setValue("office_id", o?.value, { shouldValidate: true });
                                        if (o?.value) dispatch(fetchCRManager(o?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{errors.office_id.message}</p>
                                )}
                            </div>

                            {/* CR Manager selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('logistics_manager_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_logistics_manager_placeholder')}
                                    options={cr_managers}
                                    onChange={(m) => {
                                        setValue("city_representative_id", m?.value, {
                                            shouldValidate: true,
                                        });
                                        dispatch(fetchCRTeamMember(m?.value));
                                        // dispatch(getRfqCommission(m?.value));
                                        setSelectedUsers([]);
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.city_representative_id && (
                                    <p className="text-red-500 text-sm">
                                        {errors.city_representative_id.message}
                                    </p>
                                )}
                            </div>
                            {cr_team && <div className="col-span-2">
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('logistics_member_label')}
                                </Typography>
                                <Select
                                    isDisabled={!hasAddMemberPermission}
                                    placeholder={t('select_logistics_member_placeholder')}
                                    options={cr_team}
                                    onChange={(m) => {
                                        handleChange(m?.value);
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => t('no_logistics_member_available')}

                                />
                            </div>}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('order_deadline_date_time_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="datetime-local"
                                    min={minDateTime}
                                    {...register("deadline")}
                                    className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{ className: "before:content-none after:content-none" }}
                                />
                                {errors.deadline && (
                                    <p className="text-red-500 text-sm">{errors.deadline.message}</p>
                                )}
                            </div>

                            {/* Priority */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('order_priority_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_priority_placeholder')}
                                    options={priorities}
                                    onChange={(priority) =>
                                        setValue("priority", priority?.value, { shouldValidate: true })
                                    }
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.priority && (
                                    <p className="text-red-500 text-sm">{errors.priority.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 mb-6">
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('set_commission_for_each_role_label')}
                                </Typography>
                                <div className="flex items-center gap-6 mb-3">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="percentage" {...register("commission_type")} />
                                        {t('percentage_commission_label')}
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="flat" {...register("commission_type")} />
                                        {t('flat_commission_label')}
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    {defaultCommissions.map((item, index) => (
                                        <div key={index} className="flex flex-col gap-1">
                                            <div className="bg-gray-50 rounded-md">

                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <p className="text-sm text-gray-800">{item.text}</p>
                                                    <div className="flex items-center gap-3 flex-wrap justify-end">
                                                        <div className="flex items-center gap-2">
                                                            {/* <input
                                                            step="any"
                                                            type="number"
                                                            min={"0"}
                                                            {...register(item.key)}
                                                            className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"}`}
                                                        /> */}
                                                            <input
                                                                step="any"
                                                                type="number"
                                                                min="0"
                                                                {...register(item.key)}
                                                                disabled={
                                                                    item.key === "commission_city_rep" &&
                                                                    !isCityMemberSelected
                                                                }
                                                                className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"
                                                                    } ${item.key === "commission_city_rep" &&
                                                                        !isCityMemberSelected
                                                                        ? "bg-gray-100 cursor-not-allowed"
                                                                        : ""
                                                                    }`}
                                                            />
                                                            <span className="text-gray-600 text-sm">
                                                                {watch("commission_type") === "percentage" ? "%" : "SAR"}
                                                            </span>
                                                        </div>

                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <span className="text-gray-600 text-xs whitespace-nowrap">
                                                        {t('time_slot_label')}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="datetime-local"
                                                            // disabled={!deadlineValue}
                                                            disabled={
                                                                !deadlineValue
                                                            }
                                                            min={minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.fromKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"}`}
                                                        />
                                                        <input
                                                            type="datetime-local"
                                                            // disabled={!deadlineValue}
                                                            disabled={
                                                                !deadlineValue
                                                            }
                                                            min={watch(item.fromKey) || minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.toKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"}`}
                                                        />
                                                        {(watch(item.fromKey) || watch(item.toKey)) && (
                                                            <XCircleIcon
                                                                className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                                                                onClick={() => {
                                                                    setValue(item.fromKey, "", { shouldValidate: true });
                                                                    setValue(item.toKey, "", { shouldValidate: true });
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {errors[item.key] && (
                                                <p className="text-red-500 text-xs px-4">{errors[item.key].message}</p>
                                            )}
                                            {(errors[item.fromKey] || errors[item.toKey]) && (
                                                <p className="text-red-500 text-xs px-4">
                                                    {(errors[item.fromKey] || errors[item.toKey])?.message}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {t('note_label')} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows="3"
                                    className="w-full border rounded-lg px-3 py-2"
                                    {...register("notes")}
                                    placeholder={t('note_for_logistics_placeholder')}
                                ></textarea>
                                {errors.notes && (
                                    <p className="text-red-500 text-sm">{errors.notes.message}</p>
                                )}
                            </div>

                            {/* Important Commission Notice */}
                            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                                <p className="text-sm font-medium text-amber-900">
                                    {t('important_notice_title')}
                                </p>

                                <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                                    {t('commission_deduction_notice')}
                                </p>
                            </div>
                        </div>
                    </DialogBody>
                </div>
                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
                        {/* Header with Warning Icon */}
                        <div className="flex items-center gap-2 mb-3">
                            <svg
                                className="h-5 w-5 text-amber-600 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                                />
                            </svg>
                            <h3 className="font-semibold text-amber-800">
                                {t('schedule_conflict_title')}
                            </h3>
                        </div>

                        {/* User List */}
                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                            {leaveConfirmation.map((user) => (
                                <li
                                    key={user.user_id}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4"
                                >
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-gray-900">{user.name}</span>
                                        {(user.user_type || user.role) && (
                                            <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {[user.user_type, user.role].filter(Boolean).join(" • ")}
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                        {moment(user.leave_from).format("DD MMM YYYY")} –{" "}
                                        {moment(user.leave_to).format("DD MMM YYYY")}
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Actionable Footer Copy */}
                        <p className="text-sm text-amber-800">
                            {t('schedule_conflict_instruction_1')} <strong className="font-semibold underline decoration-amber-500 underline-offset-2">{t('force_assign_button')}</strong> {t('schedule_conflict_instruction_2')}
                        </p>
                    </div>
                )}

                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {t('not_now_button')}
                    </button>
                    <button type="submit" className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium shadow-sm">
                        {t('assign_now_button')}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                        <button
                            type="button"
                            className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm"
                            onClick={() => handleForceAssign()}
                        >
                            {t('force_assign_button')}
                        </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default AssignCRManagerModal;
